import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { shellSelectors } from "store/selectors";
import { generateId } from "@cayuse/shared-react.utils.helpers";
import { alphaSortCollectionByKey, getStoredFacetedSearch, storeFacetedSearch } from "utils";
import { isFacetedSearchEmpty } from "utils/search";
import { isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { useDebounceCallback, useDictionary } from "hooks";
import CovertButton from "@cayuse/shared-react.components.covert-button";
import DateFacet from "components/FacetedSearchBar/DateFacet";
import TextFacet from "components/FacetedSearchBar/TextFacet";
import FacetMenu from "components/FacetedSearchBar/FacetMenu";
import Icon from "@cayuse/shared-react.components.icon";
import TextInput from "@cayuse/shared-react.components.form.inputs.text";

import styles from "./styles.css";

const fieldFacetHash = {
	date: DateFacet,
	datetime: DateFacet,
	text: TextFacet
};

const FacetedSearchBar = ({
	facets,
	omittedFacetTypes,
	ariaLabel,
	ariaLabelledBy,
	ariaDescribedBy,
	facetAriaControls,
	onChange,
	type,
	actualFacets,
	customStyles: {
		facetMenuWrapper = {},
		facetLabelList = {},
	} = {},
	results = [],
	isResultsLoading = false,
	onResultsSelect,
	shouldResetFacetedSearch = false,
	placeholder = "",
	onBlur = _.noop,
	onFocus = _.noop,
}) => {
	const dictionary = useDictionary("components.facetedSearchBar");

	const searchbarRef = useRef(null);
	const clearButtonRef = useRef(null);
	const menuRef = useRef(null);

	const BASE_SEARCHBAR_ID = useMemo( () => generateId("searchbar"), [] );
	const MENU_ID = BASE_SEARCHBAR_ID + "-menu";
	const ARIA_DESCRIBEDBY_ID = BASE_SEARCHBAR_ID + "-description";
	const { userId } = useSelector(shellSelectors.cayuseHeader);

	const [ search, setSearch ] = useState("");
	const [ facetSearch, setFacetSearch ] = useState({}); // object that has the facets written in the search bar component
	const [ actualFacetSearch, setActualFacetSearch ] = useState({}); //object that has all selected facets (including tiles)
	const [ isFacetMenuOpen, setIsFacetMenuOpen ] = useState(false);
	const [ justSelectedFacet, setJustSelectedFacet ] = useState(null);
	const [ activeDescendant, setActiveDescendant ] = useState("");
	const [ numberOfFacetsShown, setNumberofFacetsShown ] = useState(`Showing ${facets.length} of ${facets.length} items`);
	const [ isResultsMenuOpen, setIsResultsMenuOpen ] = useState(false);

	useEffect(() => {
		const persistedFacetSearch = getStoredFacetedSearch(type, userId);

		if (isNonEmptyObject(persistedFacetSearch)) {
			setFacetSearch(persistedFacetSearch);
		}

	}, [ ]);

	useEffect(()=>{
		const tempFacetSearch = { ...facetSearch };
		const tempActualFacets = { ...actualFacets };

		const keyList = Object.keys(tempActualFacets);
		keyList.map(key => {
			if(!(key in tempFacetSearch) || tempActualFacets[key] !== tempFacetSearch[key]) {
				tempFacetSearch[key] = tempActualFacets[key];
			}
		});
		setActualFacetSearch(tempFacetSearch);
		storeFacetedSearch(type, userId, tempFacetSearch);
	},[ actualFacets, facetSearch ]);


	useEffect(()=>{
		const isFacetedSearchOpen = !isFacetedSearchEmpty({ facetSearch: actualFacetSearch });

		setIsResultsMenuOpen(isFacetedSearchOpen);
	},[ actualFacetSearch ]);

	useEffect(()=>{
		if (shouldResetFacetedSearch) {
			resetSearch();
		}
	},[ shouldResetFacetedSearch ]);

	// ARIA attributes and management
	const ariaLabellingAttributes = {
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		"aria-describedby": ariaDescribedBy
	};

	const activeDesdendantAttribute = activeDescendant !== "" ? { "aria-activedescendant": activeDescendant } : { };

	// when the facet menu closes, reset the activeDescendant
	useEffect( () => {
		if (!isFacetMenuOpen) {
			setActiveDescendant("");
		}
	}, [ isFacetMenuOpen ]);

	const remainingFacets = useMemo(() => {
		const remainingFacets = [ ...facets ]
			.filter(({ facetType }) => !omittedFacetTypes.includes(facetType))
			.filter(({ key, text }) => {
				const isSearchMatch = !isNonEmptyString(search) || text.toLowerCase().includes(search);
				const isUsed = key in actualFacetSearch;

				return isSearchMatch && !isUsed;
			});

		alphaSortCollectionByKey(remainingFacets, "text");
		setNumberofFacetsShown(`Showing ${remainingFacets.length} of ${facets.length} items`);

		return remainingFacets;
	}, [ facets, omittedFacetTypes, search, actualFacetSearch ]);

	const hasActiveFacets = isNonEmptyObject(actualFacetSearch);

	const onUnusedFacetClick = ({ key }) => {
		const updatedFacetSearch = {
			...actualFacetSearch,
			[key]: ""
		};

		setJustSelectedFacet(key);
		setFacetSearch(updatedFacetSearch);
		setActualFacetSearch(updatedFacetSearch);
		setSearch("");
		setIsFacetMenuOpen(false);
	};

	const onFacetDelete = deletedFacet => {
		const updatedFacetSearch = { ...actualFacetSearch };

		delete updatedFacetSearch[deletedFacet.key];

		setFacetSearch(updatedFacetSearch);
		setActualFacetSearch(updatedFacetSearch);
	};

	const onSearchChange = query => {
		setSearch(query.toLowerCase());
	};

	const onSearchFocus = () => {
		setIsFacetMenuOpen(true);
		setIsResultsMenuOpen(false);
		onFocus();
	};

	const onFacetQueryChange = ({ key }, searchValue) => {
		const updatedFacetSearch = {
			...actualFacetSearch,
			[key]: searchValue
		};

		setFacetSearch(updatedFacetSearch);
		setActualFacetSearch(updatedFacetSearch);
	};

	const isDatePicker = event => {
		let isDatePicker = false;
		event.target.classList.forEach(className => {
			if (className.startsWith("react-datepicker")) {
				isDatePicker = true;
			}
		});

		return isDatePicker;
	};

	const focusSearch = event => {
		if (searchbarRef.current && !isDatePicker(event)) {
			searchbarRef.current.focus();
		}
	};

	const resetSearch = () => {
		setSearch("");
		setFacetSearch({});
		setActualFacetSearch({});
		setIsFacetMenuOpen(false);
		setActiveDescendant("");
	};

	const handleOnChange = useCallback(onChange, []);
	const debouncedOnChange = useDebounceCallback(handleOnChange);

	const facetedSearchBarOnClick = event => {
		if (isDatePicker(event)) {
			setIsFacetMenuOpen(false);
		}

		event.stopPropagation();
	};

	const closeFacetMenu = useCallback(() => setIsFacetMenuOpen(false), []);

	useEffect(() => {
		debouncedOnChange({ facetSearch, facets });
	}, [ facetSearch, facets ]);

	useEffect(() => {
		if (justSelectedFacet) {
			setJustSelectedFacet(null);
		}
	}, [ justSelectedFacet ]);

	useEffect(() => {
		const handleClick = () => {
			setIsFacetMenuOpen(false);
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, []);


	// Keyboard interactions for the faceted search bar follows the W3C recommended practices for a combobox with  an autocomplete list:
	// https://w3c.github.io/aria-practices/examples/combobox/combobox-autocomplete-list.html
	// NOTE: FacetMenu keyboard interactions and internal focus are also managed here. Individual facets are managed in their respective components
	const handleTextInputKeydown = evt => {
		const key = evt.key;
		const menuListItems = document.querySelector(".facetMenuWrapper .facetMenu ul");
		const menuItems = menuRef.current ? menuRef.current.getElementsByTagName("li") : menuListItems.getElementsByTagName("li");
		const firstMenuItem = menuItems && menuItems.item(0);
		const lastMenuItem = menuItems && menuItems.item(menuItems.length - 1);
		let activeDescendantMenuItem;

		if (activeDescendant && menuRef.current) {
			activeDescendantMenuItem = menuRef.current.querySelector(`#${activeDescendant}`);
		} else if (activeDescendant && !menuRef.current) {
			activeDescendantMenuItem = menuListItems.querySelector(`#${activeDescendant}`);
		} else {
			activeDescendantMenuItem = null;
		}

		// Next/Prev item navigation is circular, ie, when you reach the last item, jump to the first when navigating down, and vice versa
		const getNextMenuItem = () => {
			const nextMenuItem = activeDescendantMenuItem === lastMenuItem ?
				firstMenuItem : activeDescendantMenuItem.nextElementSibling;

			return nextMenuItem;
		};
		const getPreviousMenuItem = () => {
			const previousMenuItem = activeDescendantMenuItem === firstMenuItem ?
				lastMenuItem : activeDescendantMenuItem.previousElementSibling;

			return previousMenuItem;
		};

		const getSelectedFacet = () => remainingFacets.find( facet => activeDescendant.indexOf(facet.key) !== -1);

		switch (key) {
		// Down arrow opens a closed menu and focuses the FIRST item. Note alt key modifier handling below.
		// Down arrow when menu is open will cycle the activedescendant down the menu
		case "ArrowDown":
			evt.preventDefault();

			if (!isFacetMenuOpen) {
				setIsFacetMenuOpen(true);

				// if alt key is pressed with ArrowDown, only open the menu, but do not focus it
				if (evt.altKey) { return; }
			}

			// Set the next menu item. If we haven't set an activedescendant, initialize to the first item.
			const nextMenuItem = activeDescendantMenuItem ? getNextMenuItem(activeDescendantMenuItem) : firstMenuItem;

			if (nextMenuItem) {
				setActiveDescendant(nextMenuItem.id);
				nextMenuItem.scrollIntoView({block: "nearest", inline: "end"});
			}
			break;

		// Up arrow opens a closed menu and focuses the LAST item.  Up arrow when menu is open will cycle the
		// activedescendant up the menu
		case "ArrowUp":
			evt.preventDefault();
			if (!isFacetMenuOpen) {
				setIsFacetMenuOpen(true);
			}

			// Set the previous menu item. If we haven't set an activedescendant, initialize to the last item.
			const prevMenuItem = activeDescendantMenuItem ? getPreviousMenuItem(activeDescendantMenuItem) : lastMenuItem;

			if (prevMenuItem) {
				setActiveDescendant(prevMenuItem.id);
				prevMenuItem.scrollIntoView({block: "nearest", inline: "end"});
			}
			break;

		// Enter key always closes an open menu. Also, if an activedescendant has been selected, it will create that facet
		case "Enter":
			evt.preventDefault();
			if (isFacetMenuOpen) {
				setIsFacetMenuOpen(false);
			}

			// if we have an activedescendant when enter is pressed, add that facet
			if (activeDescendant) {
				const selectedFacet = getSelectedFacet();

				onUnusedFacetClick(selectedFacet);
			}
			break;

		// Escape key closes an open menu when search input is focused, or clears the search if the menu is closed
		case "Escape":
			evt.preventDefault();
			if (isFacetMenuOpen) {
				setIsFacetMenuOpen(false);
			} else {
				resetSearch();
			}
			break;

		// Tab key should move focus to the next controllable item. Close the menu in case the user is tabbing out of the search
		// We should NOT prevent the default action of the tab key.
		case "Tab":
			setIsFacetMenuOpen(false);
			break;

		default:
			break;
		}
	};

	const renderFacetMenuStatusAlert = () => {
		return isFacetMenuOpen ? <div role="alert" aria-live="assertive" className="accessibility-text">{numberOfFacetsShown}</div> : "";
	};

	const handleOnResultsSelect = ({ key }) => {
		onResultsSelect(key);
	};

	return (
		<div data-component="FacetedSearchBar" className={styles.facetedSearchBar} onClick={facetedSearchBarOnClick} role="application" aria-roledescription="search bar">
			<div className={styles.inputWrapper} onClick={focusSearch}>
				<Icon name="search" className={styles.searchIcon}/>
				<div className={styles.facetLabelContainer}>
					<ul className={styles.facetLabelList} style={facetLabelList}>
						{hasActiveFacets && Object.keys(actualFacetSearch).map(key => {
							const facet = facets.find(facet => facet.key === key);
							const searchValue = actualFacetSearch[key];
							const facetType = facet?.facetType || "text";
							const FacetInput = facetType in fieldFacetHash
								? fieldFacetHash[facetType]
								: TextFacet;

							return facet
								? (
									<li key={`facet-label-input-${key}`}>
										<FacetInput
											text={facet.text}
											query={searchValue}
											onChange={({ query, searchValue }) => {
												const valueToSearch = isNonEmptyString(searchValue)
													? searchValue
													: query;
												onFacetQueryChange(facet, valueToSearch);
											}}
											onDelete={() => onFacetDelete(facet)}
											shouldFocus={facet.key === justSelectedFacet}
											onClick={closeFacetMenu}
											facetAriaControls={facetAriaControls}
										/>
									</li>
								)
								: "";
						})}
					</ul>
				</div>
				{renderFacetMenuStatusAlert()}

				<TextInput
					placeholder={placeholder}
					id={BASE_SEARCHBAR_ID}
					className={hasActiveFacets
						? styles.facetSearch
						: styles.firstFacetSearch}
					variant="invisible"
					role="combobox"
					{...ariaLabellingAttributes}
					{...activeDesdendantAttribute}
					aria-autocomplete="list"
					aria-controls={MENU_ID}
					aria-expanded={isFacetMenuOpen}
					aria-describedby={ARIA_DESCRIBEDBY_ID}
					onChange={event => onSearchChange(event.target.value)}
					onFocus={onSearchFocus}
					onBlur={onBlur}
					onKeyDown={handleTextInputKeydown}
					ref={searchbarRef}
					value={search}
				/>
				<CovertButton
					aria-label={dictionary.searchButtonAriaLabel || "Clear search"}
					ref={clearButtonRef} onClick={() => resetSearch()}
					className={styles.facetedSearchClearButton}
					tabIndex="0"
				>
					<Icon name="times" className={styles.clearIcon}/>
				</CovertButton>
				<div id={ARIA_DESCRIBEDBY_ID} className="accessibility-text">
					<h2>Search Keyboard Accessibility</h2>
					<p>
						This search works by selecting filters that will be applied to the data. Use the
						following keyboard interactions to navigate the search.
					</p>
					<h3>Selecting a Filter</h3>
					<ul>
						<li>The filter menu is a list of categories that can be used to filter the data.  It opens automatically when this item is focused.</li>
						<li>Type text to narrow the filter menu. The filter menu will reduce to all items that contain the text entered.</li>
						<li>Down arrow will navigate the filter menu first item to last. When the last item is reached, navigation returns to the first.</li>
						<li>Up arrow will navigate the filter menu last item to first.  When the first item is reached, navigation returns to the last.</li>
						<li>Enter will select a filter, close the filter menu, and focus the selected filter for input.</li>
						<li>Escape will close the filter menu if it is open. If the menu is not open, escape will reset the search.</li>
						<li>Tab and shift plus tab will navigate the page focus to the next item on the page if no filter has been selected.</li>
						<li>
							Tab and shift plus tab will also navigate the selected filters if they are present.  Note that if you are
							currently in filter selection mode, shift plus tab will take you to the remove button of the last filter selected.
						</li>
						<li>Alt plus down arrow will open the filter menu if it is closed.</li>
					</ul>
					<h3>Using a Filter</h3>
					<ul>
						<li>Once a filter is selected, you may start typing text. Data will be immediately filtered based on your text.</li>
						<li>Some filters may require you to select a date (or date and/or time) from a calendar instead of entering text.  Data is immediately filtered upon selection.</li>
						<li>To remove a filter, tab to its remove button and press enter. Data will be updated immediately after the filter deletion.</li>
					</ul>
				</div>
			</div>

			<div className={`${styles.facetMenuWrapper} facetMenuWrapper`} style={facetMenuWrapper}>
				<FacetMenu
					menuId={MENU_ID}
					baseSearchbarId={BASE_SEARCHBAR_ID || "searchbar"}
					facets={remainingFacets}
					isOpen={isFacetMenuOpen}
					onSelect={onUnusedFacetClick}
					currentlySelectedItem={activeDescendant}
					ref={menuRef}
					onFocus={onFocus}
					onBlur={onBlur}
				/>
			</div>

			{ isResultsMenuOpen &&
				<div className={styles.facetMenuWrapper} style={facetMenuWrapper}>
					<FacetMenu
						menuId={MENU_ID}
						baseSearchbarId={BASE_SEARCHBAR_ID || "searchbar"}
						facets={results}
						isOpen={isResultsMenuOpen}
						onSelect={handleOnResultsSelect}
						currentlySelectedItem={activeDescendant}
						isLoading={isResultsLoading}
						ref={menuRef}
						onFocus={onFocus}
						onBlur={onBlur}
					/>
				</div>
			}
		</div>
	);
};

FacetedSearchBar.defaultProps = {
	omittedFacetTypes: []
};

FacetedSearchBar.propTypes = {
	facets: PropTypes.arrayOf(
		PropTypes.shape({
			fieldKeys: PropTypes.arrayOf(PropTypes.string),
			key: PropTypes.string,
			keys: PropTypes.arrayOf(PropTypes.string),
			text: PropTypes.shape,
			query: PropTypes.string,
			value: PropTypes.string,
			facetType: PropTypes.oneOf([ "string", "text" ])
		})
	),
	omittedFacetTypes: PropTypes.arrayOf(PropTypes.string),
	ariaLabel: PropTypes.string,
	ariaLabelledBy: PropTypes.string,
	ariaDescribedBy: PropTypes.string,
	facetAriaControls: PropTypes.string,
	type: PropTypes.string,
	onChange: PropTypes.func
};

export default FacetedSearchBar;
